import axios, { type AxiosResponse, type InternalAxiosRequestConfig, type AxiosError } from 'axios';
import { isEmpty } from 'lodash-es';
import { useAuthStore } from '@/stores/authStore';

declare module 'axios' {
  export interface AxiosRequestConfig {
    requiresAuth?: boolean;
    requiresAcceptedTou?: boolean;
  }
}

function getAuthToken(): string | undefined {
  const authStore = useAuthStore();
  return authStore.token ?? undefined;
}

function setAuthHeader(config: InternalAxiosRequestConfig, token?: string) {
  if (!isEmpty(token)) config.headers.set('Authorization', `Bearer ${token}`);
  return config;
}

function onRequestFulfilled(config: InternalAxiosRequestConfig) {
  if (config.requiresAuth) {
    config = setAuthHeader(config, getAuthToken());
  }

  return config;
}

function onResponseFulfilled(response: AxiosResponse) {
  return response;
}

function onResponseRejected(error: AxiosError) {
  // Don't handle errors here - let TanStack Query handle them
  // This prevents duplicate toasts on retries
  // TanStack Query's QueryCache and defaultOptions handle all errors
  return Promise.reject(error);
}

let mockReadiness: Promise<void> = Promise.resolve();

const initAxios = () => {
  axios.defaults.baseURL = import.meta.env.VITE_API_BASE;
  axios.defaults.requiresAuth = true;
  axios.defaults.requiresAcceptedTou = true;
  axios.interceptors.request.use(onRequestFulfilled);
  axios.interceptors.response.use(onResponseFulfilled, onResponseRejected);

  installDevMocks();
};

function installDevMocks() {
  mockReadiness = Promise.resolve();
}

export async function waitForDevMocks(): Promise<void> {
  // To mock an endpoint that is not on this environment yet, assign the dynamic
  // import before the await. For example:
  // mockReadiness = import('@/utils/mocks/exampleEndpoint').then(async ({ installExampleEndpointMock }) => {
  //   await installExampleEndpointMock();
  // });

  await mockReadiness;
}

export { initAxios };
